import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { 
  ArrowLeft, Star, MapPin, Calendar, Package, MessageCircle, 
  Phone, Shield, CheckCircle, Store, Users, Award, Clock,
  ChevronRight, MessageSquare, ThumbsUp, Eye, Heart, Share2,
  Home, User, Bell, Menu, ChevronLeft, Search, Filter
} from 'lucide-react';
import { 
  getSellerProfile, 
  getSellerStats, 
  getListings, 
  getSellerAllRatings,
  getListingRatings
} from '../../services/api';
import { getImageUrl } from '../../utils/imageUtils';
import { formatPrice, formatDate } from '../../utils/listingUtils';
import Loading from '../../components/common/Loading';
import ReviewItem from '../../components/listing/ReviewItem';
import ProductGrid from '../home/ProductGrid';

// Import Header and Footer
import Header from '../../components/layout/Header';
import Footer from '../../components/layout/Footer';

const SellerDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [seller, setSeller] = useState(null);
  const [stats, setStats] = useState(null);
  const [listings, setListings] = useState([]);
  const [reviews, setReviews] = useState([]);
  const [reviewsLoading, setReviewsLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('listings');
  const [loading, setLoading] = useState(true);
  const [listingsFilter, setListingsFilter] = useState('active');
  const [showAllReviews, setShowAllReviews] = useState(false);
  const [isFavorited, setIsFavorited] = useState(false);
  
  // ProductGrid state
  const [viewMode, setViewMode] = useState('grid');
  const [sortBy, setSortBy] = useState('newest');
  const [currentPage, setCurrentPage] = useState(1);

  useEffect(() => {
    fetchSellerData();
  }, [id]);

  const fetchSellerData = async () => {
    setLoading(true);
    try {
      // Fetch seller details
      const sellerResponse = await getSellerProfile(id);
      setSeller(sellerResponse.data?.seller || sellerResponse);

      // Fetch seller stats
      const statsResponse = await getSellerStats(id);
      setStats(statsResponse.data || statsResponse);

      // Fetch seller's active listings
      const listingsResponse = await getListings({ 
        seller_id: id, 
        status: 'active',
      });
      setListings(listingsResponse.data || listingsResponse || []);

    } catch (error) {
      // console.error('Error fetching seller data:', error);
    } finally {
      setLoading(false);
    }
  };

  // Fetch reviews when Reviews tab is active
  useEffect(() => {
    if (activeTab === 'reviews' && seller) {
      fetchSellerReviews();
    }
  }, [activeTab, seller]);

  const fetchSellerReviews = async () => {
    if (!seller) return;
    
    setReviewsLoading(true);
    try {
      const reviewsResponse = await getSellerAllRatings(seller.user_id || seller.seller_id, 50, 0);
      
      if (reviewsResponse.success && reviewsResponse.data) {
        setReviews(reviewsResponse.data);
      } else if (Array.isArray(reviewsResponse)) {
        setReviews(reviewsResponse);
      } else {
        // console.warn('No reviews data returned:', reviewsResponse);
        setReviews([]);
      }
    } catch (error) {
      // console.error('Error fetching seller reviews:', error);
      setReviews([]);
    } finally {
      setReviewsLoading(false);
    }
  };

  const formatReviewData = (review) => {
    return {
      ...review,
      rating_id: review.rating_id || review.id,
      rating_overall: review.rating_overall || 0,
      rating_quality: null,
      rating_communication: null,
      rating_delivery: null,
      review_text: review.review_text || '',
      buyer_name: review.buyer_first_name && review.buyer_last_name 
        ? `${review.buyer_first_name} ${review.buyer_last_name}`
        : review.buyer_name || review.full_name || 'Anonymous',
      buyer_profile_picture: review.buyer_profile_picture || review.profile_picture_url || '',
      is_verified_purchase: false,
      created_at: review.created_at || review.review_date,
      status: review.status || 'approved',
      helpful_count: review.helpful_count || 0,
      listing_title: review.listing_title || 'Item',
      time_ago: review.time_ago || formatTimeAgo(review.created_at)
    };
  };

  const formatTimeAgo = (dateString) => {
    if (!dateString) return '';
    
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);
    
    if (diffMins < 60) {
      return `${diffMins}m ago`;
    } else if (diffHours < 24) {
      return `${diffHours}h ago`;
    } else if (diffDays < 30) {
      return `${diffDays}d ago`;
    } else {
      return formatDate(dateString);
    }
  };

  const handleContactSeller = () => {
    if (!seller) return;
    
    if (listings.length > 0) {
      navigate(`/listings/${listings[0].listing_id}`);
    } else {
      alert(`Contact ${seller.store_name || seller.first_name} via message`);
    }
  };

  const handleShare = () => {
    if (!seller) return;
    
    const shareUrl = `${window.location.origin}/seller/${id}`;
    const shareText = `Check out ${seller.store_name || seller.first_name}'s profile on EcoMart!`;
    
    if (navigator.share) {
      navigator.share({
        title: `${seller.store_name || seller.first_name}'s Profile`,
        text: shareText,
        url: shareUrl,
      });
    } else {
      navigator.clipboard.writeText(shareUrl)
        .then(() => alert('Profile link copied to clipboard!'))
    }
  };

  const handleToggleFavorite = () => {
    if (!seller) return;
    setIsFavorited(!isFavorited);
  };

  const handleViewListing = (listingId) => {
    navigate(`/listings/${listingId}`);
  };

  const getSafeRating = (ratingValue) => {
    if (ratingValue === null || ratingValue === undefined || isNaN(ratingValue)) {
      return 0;
    }
    
    const numericRating = typeof ratingValue === 'string' ? parseFloat(ratingValue) : ratingValue;
    return isNaN(numericRating) ? 0 : numericRating;
  };

  const renderStars = (rating, size = 'w-5 h-5') => {
    const safeRating = getSafeRating(rating);
    
    return (
      <div className="flex items-center">
        {[...Array(5)].map((_, index) => (
          <Star
            key={index}
            className={`${size} ${
              index < Math.floor(safeRating)
                ? 'fill-current text-yellow-400'
                : 'text-gray-300'
            }`}
          />
        ))}
        <span className="ml-2 font-bold text-black">
          {safeRating.toFixed(1)}
        </span>
      </div>
    );
  };

  const calculateAverageRating = () => {
    if (reviews.length === 0) return 0;
    
    const validRatings = reviews
      .map(r => r.rating_overall)
      .filter(rating => rating && rating > 0);
    
    if (validRatings.length === 0) return 0;
    
    const total = validRatings.reduce((sum, rating) => sum + rating, 0);
    return (total / validRatings.length).toFixed(1);
  };

  const renderStatCard = (icon, label, value, valueColor = "text-black") => (
    <div className="bg-white rounded-xl p-4 shadow-sm border border-gray-100">
      <div className="flex items-center gap-3">
        <div className="p-2 bg-gradient-to-br from-orange-100 to-orange-200 rounded-lg text-[#ff660e]">
          {icon}
        </div>
        <div>
          <p className={`text-xl font-bold ${valueColor}`}>
            {typeof value === "number" ? value.toLocaleString() : value}
          </p>
          <p className="text-xs text-gray-600 mt-0.5">{label}</p>
        </div>
      </div>
    </div>
  );

  const getTotalReviewsCount = () => {
    return stats?.summary?.total_ratings || reviews.length || 0;
  };

  const getRecentReviewsCount = () => {
    const thirtyDaysAgo = new Date();
    thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);
    
    return reviews.filter(review => {
      const reviewDate = new Date(review.created_at);
      return reviewDate > thirtyDaysAgo;
    }).length;
  };

  const getFilteredListings = () => {
    let filtered = [...listings];
    
    switch (listingsFilter) {
      case 'featured':
        filtered = filtered.filter(listing => listing.is_boosted);
        break;
      case 'recent':
        filtered = filtered.sort((a, b) => 
          new Date(b.created_at) - new Date(a.created_at)
        );
        break;
      case 'active':
      default:
        break;
    }
    
    return filtered;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <Loading text="Loading seller profile..." />
      </div>
    );
  }

  if (!seller) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-black mb-4">Seller Not Found</h2>
          <p className="text-gray-600 mb-6">The seller profile you're looking for doesn't exist.</p>
          <button
            onClick={() => navigate(-1)}
            className="bg-[#ff660e] text-white px-6 py-3 rounded-lg font-semibold hover:bg-[#ff5500]"
          >
            Go Back
          </button>
        </div>
      </div>
    );
  }

  const sellerName = seller.store_name || `${seller.first_name} ${seller.last_name}`;
  const rating = getSafeRating(stats?.summary?.avg_overall || calculateAverageRating());
  const totalReviews = getTotalReviewsCount();
  const recentReviews = getRecentReviewsCount();
  const filteredListings = getFilteredListings();

  // ========== MOBILE VIEW ==========
  const MobileView = () => (
    <div className="lg:hidden min-h-screen bg-gray-50 pb-20">
      {/* Mobile Header */}
      <header className="bg-white shadow-sm sticky top-0 z-50">
        <div className="px-4 py-3">
          <div className="flex items-center justify-between">
            <button
              onClick={() => navigate(-1)}
              className="flex items-center gap-2 p-2 rounded-lg hover:bg-gray-100"
            >
              <ArrowLeft className="w-5 h-5 text-black" />
              <span className="font-medium text-black">Back</span>
            </button>
            
            <div className="flex items-center gap-2">
              <button 
                onClick={handleToggleFavorite}
                className={`p-2 rounded-full ${
                  isFavorited 
                    ? 'text-red-500 hover:bg-red-50' 
                    : 'text-gray-600 hover:text-[#ff660e] hover:bg-orange-50'
                }`}
              >
                <Heart className={`w-5 h-5 ${isFavorited ? 'fill-current' : ''}`} />
              </button>
              <button 
                onClick={handleShare}
                className="p-2 text-gray-600 hover:text-[#ff660e] rounded-full hover:bg-orange-50"
              >
                <Share2 className="w-5 h-5" />
              </button>
            </div>
          </div>
        </div>
      </header>

      <main className="px-4 py-4">
        {/* Seller Profile Header - Mobile */}
        <div className="bg-white rounded-xl shadow-sm p-4 mb-4">
          <div className="flex items-start gap-3">
            {/* Seller Avatar */}
            <div className="relative">
              {seller.store_logo_url ? (
                <img
                  src={getImageUrl(seller.store_logo_url)}
                  alt={sellerName}
                  className="w-16 h-16 rounded-full border-2 border-white shadow-sm object-cover"
                />
              ) : (
                <div className="w-16 h-16 rounded-full bg-gradient-to-r from-[#ff660e] to-red-500 flex items-center justify-center text-white font-bold text-2xl shadow-sm">
                  {sellerName.charAt(0).toUpperCase()}
                </div>
              )}
              
              {/* Verification Badges */}
              {seller.is_email_verified && (
                <div className="absolute -bottom-1 -right-1 bg-green-500 text-white p-1 rounded-full">
                  <CheckCircle className="w-3 h-3" />
                </div>
              )}
            </div>

            {/* Seller Info */}
            <div className="flex-1">
              <h1 className="text-lg font-bold text-black mb-1">{sellerName}</h1>
              {seller.store_name && seller.first_name && (
                <p className="text-sm text-gray-600 mb-2">
                  By {seller.first_name} {seller.last_name}
                </p>
              )}
              
              {/* Rating */}
              <div className="flex items-center gap-2">
                {renderStars(rating, 'w-4 h-4')}
                <span className="text-sm text-gray-600">({totalReviews})</span>
              </div>
              
              {/* Location */}
              <div className="flex items-center text-gray-600 mt-2 text-sm">
                <MapPin className="w-3 h-3 mr-1" />
                <span>{seller.store_location || seller.location || 'Location not specified'}</span>
              </div>
            </div>
          </div>

          {/* Store Tagline */}
          {seller.store_tagline && (
            <p className="text-gray-600 text-sm italic mt-3 border-t pt-3">
              {seller.store_tagline}
            </p>
          )}

          {/* Store Description - Collapsible */}
          {seller.store_description && (
            <div className="mt-3 border-t pt-3">
              <h4 className="font-medium text-black text-sm mb-2">About This Store</h4>
              <p className="text-gray-700 text-sm line-clamp-8">
                {seller.store_description}
              </p>
            </div>
          )}
        </div>

        {/* Stats Grid - Mobile */}
        <div className="grid grid-cols-2 gap-3 mb-4">
          {renderStatCard(
            <Package className="w-4 h-4" />,
            'Listings',
            listings.length
          )}
          {renderStatCard(
            <Users className="w-4 h-4" />,
            'Reviews',
            totalReviews
          )}
          {renderStatCard(
            <Award className="w-4 h-4" />,
            'Rating',
            rating.toFixed(1)
          )}
          {renderStatCard(
            <Clock className="w-4 h-4" />,
            'Response',
            stats?.response_rate ? `${stats.response_rate}%` : 'N/A'
          )}
        </div>

        {/* Tabs - Mobile */}
        <div className="flex space-x-1 bg-gray-100 p-1 rounded-xl mb-4">
          <button
            onClick={() => setActiveTab('listings')}
            className={`flex-1 py-2.5 px-3 rounded-lg flex items-center justify-center gap-2 transition-colors ${
              activeTab === 'listings'
                ? 'bg-white shadow text-[#ff660e]'
                : 'text-gray-600 hover:text-gray-900'
            }`}
          >
            <Store className="w-4 h-4" />
            <span className="text-sm font-medium">Listings</span>
          </button>
          <button
            onClick={() => setActiveTab('reviews')}
            className={`flex-1 py-2.5 px-3 rounded-lg flex items-center justify-center gap-2 transition-colors ${
              activeTab === 'reviews'
                ? 'bg-white shadow text-[#ff660e]'
                : 'text-gray-600 hover:text-gray-900'
            }`}
          >
            <Star className="w-4 h-4" />
            <span className="text-sm font-medium">Reviews</span>
          </button>
        </div>

        {/* Tab Content */}
        {activeTab === 'listings' ? (
          <div>
            {/* Listings Filter - Mobile */}
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2">
                <select 
                  value={listingsFilter}
                  onChange={(e) => setListingsFilter(e.target.value)}
                  className="border border-gray-300 rounded-lg px-3 py-1.5 text-sm focus:outline-none focus:ring-1 focus:ring-[#ff660e] text-black"
                >
                  <option value="active">Active</option>
                  <option value="featured">Featured</option>
                  <option value="recent">Recent</option>
                </select>
              </div>
              <span className="text-sm text-gray-600">{filteredListings.length} items</span>
            </div>

            {/* Listings Grid - Mobile */}
            <div className="grid grid-cols-2 gap-3">
              {filteredListings.map((listing) => (
                <div 
                  key={listing.listing_id}
                  className="bg-white rounded-xl shadow-sm overflow-hidden border border-gray-100"
                  onClick={() => handleViewListing(listing.listing_id)}
                >
                  {/* Listing Image */}
                  <div className="aspect-square relative overflow-hidden bg-gray-100">
                    {listing.images && listing.images.length > 0 ? (
                      <img
                        src={getImageUrl(listing.images[0].image_url)}
                        alt={listing.title}
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center">
                        <Package className="w-8 h-8 text-gray-400" />
                      </div>
                    )}
                    {listing.is_boosted && (
                      <div className="absolute top-2 left-2 bg-gradient-to-r from-yellow-500 to-[#ff660e] text-white px-2 py-0.5 rounded-full text-xs font-bold">
                        ⚡
                      </div>
                    )}
                  </div>
                  
                  {/* Listing Info */}
                  <div className="p-3">
                    <h3 className="font-medium text-black text-sm mb-1 line-clamp-1">
                      {listing.title}
                    </h3>
                    <p className="text-lg font-bold text-[#ff660e]">
                      {formatPrice(listing.price)}
                    </p>
                    <div className="flex items-center text-gray-600 text-xs mt-1">
                      <MapPin className="w-3 h-3 mr-1" />
                      <span className="truncate">{listing.state_name}</span>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* No Listings Message */}
            {filteredListings.length === 0 && (
              <div className="text-center py-8">
                <Package className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                <p className="text-gray-600">No listings found</p>
              </div>
            )}
          </div>
        ) : (
          /* Reviews Tab - Mobile */
          <div className="bg-white rounded-xl shadow-sm p-4">
            {reviewsLoading ? (
              <div className="flex justify-center py-8">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#ff660e] mx-auto"></div>
              </div>
            ) : reviews.length === 0 ? (
              <div className="text-center py-6">
                <Star className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                <h3 className="text-lg font-semibold text-black mb-2">No reviews yet</h3>
                <p className="text-gray-600 text-sm">This seller hasn't received any reviews yet.</p>
              </div>
            ) : (
              <div className="space-y-4">
                {/* Rating Summary - Mobile */}
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center">
                    <Star className="w-6 h-6 fill-current text-yellow-400" />
                    <span className="text-2xl font-bold ml-2">{calculateAverageRating()}</span>
                    <span className="text-gray-500 ml-1">/5</span>
                  </div>
                  <div className="text-right">
                    <p className="text-sm text-gray-700">{reviews.length} reviews</p>
                    {recentReviews > 0 && (
                      <p className="text-xs text-gray-500">{recentReviews} recent</p>
                    )}
                  </div>
                </div>

                {/* Reviews List - Mobile */}
                <div className="space-y-4">
                  {(showAllReviews ? reviews : reviews.slice(0, 3)).map((review) => (
                    <div key={review.rating_id} className="border-b border-gray-100 pb-4 last:border-0">
                      <ReviewItem review={formatReviewData(review)} />
                    </div>
                  ))}
                  
                  {/* Load More Button */}
                  {reviews.length > 3 && (
                    <button
                      onClick={() => setShowAllReviews(!showAllReviews)}
                      className="w-full border border-gray-300 text-gray-700 py-2.5 rounded-lg text-sm font-medium hover:bg-gray-50 transition-colors"
                    >
                      {showAllReviews ? 'Show Less' : `Show ${reviews.length - 3} More`}
                    </button>
                  )}
                </div>
              </div>
            )}
          </div>
        )}

        {/* Delivery Info - Mobile */}
        {seller.delivery_options && (
          <div className="bg-white rounded-xl shadow-sm p-4 mt-4">
            <h3 className="font-bold text-black mb-3">Delivery Information</h3>
            <div className="space-y-3">
              <div>
                <h4 className="text-sm text-gray-600 mb-1">Delivery Options</h4>
                <p className="text-black text-sm capitalize">{seller.delivery_options}</p>
              </div>
              <div>
                <h4 className="text-sm text-gray-600 mb-1">Delivery Fee</h4>
                <p className="text-black text-sm">
                  {seller.delivery_fee && parseFloat(seller.delivery_fee) > 0 
                    ? `₦${parseFloat(seller.delivery_fee).toLocaleString()}` 
                    : 'Free delivery'}
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Join Date - Mobile */}
        <div className="bg-white rounded-xl shadow-sm p-4 mt-4">
          <div className="flex items-center text-gray-600">
            <Calendar className="w-4 h-4 mr-2" />
            <span className="text-sm">Seller since {formatDate(seller.created_at || seller.user_created_at)}</span>
          </div>
        </div>
      </main>

      {/* Mobile Bottom Action Bar */}
      <div className="fixed bottom-0 left-0 right-0 bg-white border-t shadow-lg p-3 z-50">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-xs text-gray-500">Seller Rating</p>
            <div className="flex items-center">
              {renderStars(rating, 'w-3 h-3')}
            </div>
          </div>
          <div className="flex gap-2">
            {seller.phone_number && (
              <a 
                href={`tel:${seller.phone_number}`}
                className="flex items-center justify-center gap-1 px-3 py-2.5 border-2 border-[#ff660e] text-[#ff660e] rounded-lg text-sm font-semibold"
              >
                <Phone className="w-4 h-4" />
                Call
              </a>
            )}
            <button 
              onClick={handleContactSeller}
              className="flex items-center justify-center gap-1 px-4 py-2.5 bg-gradient-to-r from-[#ff660e] to-red-500 text-white rounded-lg font-semibold text-sm"
            >
              <MessageCircle className="w-4 h-4" />
              Contact
            </button>
          </div>
        </div>
      </div>
    </div>
  );

  // ========== DESKTOP VIEW ==========
  const DesktopView = () => (
    <div className="hidden lg:block min-h-screen bg-gray-50">
      {/* Desktop Header */}
      <Header />
      
      <main className="max-w-7xl mx-auto px-6 py-8">
        {/* Seller Profile Header */}
        <div className="bg-white rounded-2xl shadow-lg p-6 mb-6">
          <div className="flex flex-col md:flex-row gap-6">
            {/* Seller Avatar */}
            <div className="flex-shrink-0">
              <div className="relative">
                {seller.store_logo_url ? (
                  <img
                    src={getImageUrl(seller.store_logo_url)}
                    alt={sellerName}
                    className="w-32 h-32 rounded-full border-4 border-white shadow-lg object-cover"
                  />
                ) : (
                  <div className="w-32 h-32 rounded-full bg-gradient-to-r from-[#ff660e] to-red-500 flex items-center justify-center text-white font-bold text-4xl shadow-lg">
                    {sellerName.charAt(0).toUpperCase()}
                  </div>
                )}
                
                {/* Verification Badges */}
                <div className="absolute -bottom-2 -right-2 flex gap-1">
                  {seller.is_email_verified && (
                    <div className="bg-green-500 text-white p-2 rounded-full" title="Email Verified">
                      <CheckCircle className="w-5 h-5" />
                    </div>
                  )}
                  {seller.is_phone_verified && (
                    <div className="bg-blue-500 text-white p-2 rounded-full" title="Phone Verified">
                      <Shield className="w-5 h-5" />
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Seller Info */}
            <div className="flex-1">
              <div className="flex flex-col md:flex-row md:items-start justify-between gap-4 mb-4">
                <div>
                  <h1 className="text-2xl lg:text-3xl font-bold text-black mb-2">
                    {sellerName}
                  </h1>
                  {seller.store_name && seller.first_name && (
                    <p className="text-gray-700">
                      By {seller.first_name} {seller.last_name}
                    </p>
                  )}
                  {seller.store_tagline && (
                    <p className="text-gray-600 italic mt-1">{seller.store_tagline}</p>
                  )}
                </div>
                
                {/* Rating */}
                <div className="flex items-center gap-4">
                  <div className="text-right">
                    {renderStars(rating, 'w-6 h-6')}
                    <div className="mt-1">
                      <p className="text-sm text-gray-600">{totalReviews} reviews</p>
                      {recentReviews > 0 && (
                        <p className="text-xs text-gray-500">{recentReviews} recent</p>
                      )}
                    </div>
                  </div>
                </div>
              </div>

              {/* Meta Info */}
              <div className="flex flex-wrap gap-4 mb-4">
                <div className="flex items-center text-gray-600">
                  <MapPin className="w-4 h-4 mr-2" />
                  <span>{seller.store_location || seller.location || 'Location not specified'}</span>
                </div>
                <div className="flex items-center text-gray-600">
                  <Calendar className="w-4 h-4 mr-2" />
                  <span>Seller since {formatDate(seller.created_at || seller.user_created_at)}</span>
                </div>
                {seller.phone_number && (
                  <div className="flex items-center text-gray-600">
                    <Phone className="w-4 h-4 mr-2" />
                    <span>{seller.phone_number}</span>
                  </div>
                )}
              </div>

              {/* Store Description */}
              {seller.store_description && (
                <div className="mb-6">
                  <h4 className="font-medium text-black mb-2">About This Store</h4>
                  <p className="text-gray-700 whitespace-pre-line">{seller.store_description}</p>
                </div>
              )}

              {/* Stats Grid */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                {renderStatCard(
                  <Package className="w-6 h-6" />,
                  'Listings',
                  listings.length
                )}
                {renderStatCard(
                  <Users className="w-6 h-6" />,
                  'Reviews',
                  totalReviews
                )}
                {renderStatCard(
                  <Award className="w-6 h-6" />,
                  'Rating',
                  rating.toFixed(1)
                )}
                {renderStatCard(
                  <Clock className="w-6 h-6" />,
                  'Response Rate',
                  stats?.response_rate ? `${stats.response_rate}%` : 'N/A'
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex space-x-2 bg-gray-100 p-1 rounded-xl mb-6">
          <button
            onClick={() => setActiveTab('listings')}
            className={`flex-1 py-3 px-4 rounded-lg flex items-center justify-center gap-2 transition-colors ${
              activeTab === 'listings'
                ? 'bg-white shadow text-[#ff660e]'
                : 'text-gray-600 hover:text-gray-900'
            }`}
          >
            <Store className="w-5 h-5" />
            <span className="font-medium">Listings ({listings.length})</span>
          </button>
          <button
            onClick={() => setActiveTab('reviews')}
            className={`flex-1 py-3 px-4 rounded-lg flex items-center justify-center gap-2 transition-colors ${
              activeTab === 'reviews'
                ? 'bg-white shadow text-[#ff660e]'
                : 'text-gray-600 hover:text-gray-900'
            }`}
          >
            <Star className="w-5 h-5" />
            <span className="font-medium">Reviews ({totalReviews})</span>
          </button>
        </div>

        {/* Tab Content */}
        {activeTab === 'listings' ? (
          <div>
            {/* Listings Filter */}
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center gap-2">
                <select 
                  value={listingsFilter}
                  onChange={(e) => setListingsFilter(e.target.value)}
                  className="border border-gray-300 rounded-lg px-4 py-2 focus:outline-none focus:ring-2 focus:ring-[#ff660e] text-black font-medium"
                >
                  <option value="active">Active Listings</option>
                  <option value="featured">Featured</option>
                  <option value="recent">Most Recent</option>
                </select>
              </div>
              <span className="text-sm text-gray-600">{filteredListings.length} items</span>
            </div>

            {/* ProductGrid Component */}
            <ProductGrid
              listings={filteredListings}
              viewMode={viewMode}
              sortBy={sortBy}
              currentPage={currentPage}
              onViewModeChange={setViewMode}
              onSortChange={setSortBy}
              onPageChange={setCurrentPage}
              showControls={true}
              isSearchPage={false}
              hidePagination={false}
            />
          </div>
        ) : (
          /* Reviews Tab */
          <div className="bg-white rounded-2xl shadow-lg p-6">
            {reviewsLoading ? (
              <div className="flex justify-center py-12">
                <div className="text-center">
                  <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-[#ff660e] mx-auto mb-4"></div>
                  <p className="text-gray-600">Loading reviews...</p>
                </div>
              </div>
            ) : reviews.length === 0 ? (
              <div className="text-center py-12">
                <Star className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                <h3 className="text-lg font-semibold text-black mb-2">No reviews yet</h3>
                <p className="text-gray-600 mb-4">This seller hasn't received any reviews yet.</p>
                <p className="text-sm text-gray-500">Be the first to review this seller after a transaction!</p>
              </div>
            ) : (
              <div className="space-y-6">
                {/* Rating Summary */}
                <div className="flex items-center gap-6 mb-6">
                  <div className="flex items-center">
                    <Star className="w-8 h-8 fill-current text-yellow-400" />
                    <span className="text-3xl font-bold ml-3">{calculateAverageRating()}</span>
                    <span className="text-gray-500 ml-1">/5</span>
                  </div>
                  <div>
                    <p className="text-gray-700">
                      Based on {reviews.length} review{reviews.length !== 1 ? 's' : ''}
                    </p>
                    {recentReviews > 0 && (
                      <p className="text-sm text-gray-500 mt-1">
                        {recentReviews} review{recentReviews !== 1 ? 's' : ''} in the last 30 days
                      </p>
                    )}
                  </div>
                </div>

                {/* Distribution Stats */}
                {stats?.distribution && stats.distribution.length > 0 && (
                  <div className="mb-6 p-4 bg-gray-50 rounded-lg">
                    <h4 className="font-medium text-black mb-3">Rating Distribution</h4>
                    <div className="space-y-2">
                      {[5, 4, 3, 2, 1].map((star) => {
                        const distribution = stats.distribution.find(d => d.rating_overall === star);
                        const count = distribution?.count || 0;
                        const percentage = reviews.length > 0 ? (count / reviews.length) * 100 : 0;
                        
                        return (
                          <div key={star} className="flex items-center">
                            <div className="w-12 flex items-center">
                              <Star className="w-4 h-4 fill-current text-yellow-400" />
                              <span className="ml-1 text-sm">{star}</span>
                            </div>
                            <div className="flex-1 ml-4">
                              <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
                                <div 
                                  className="h-full bg-yellow-400 rounded-full"
                                  style={{ width: `${percentage}%` }}
                                />
                              </div>
                            </div>
                            <div className="w-12 text-right text-sm text-gray-600">
                              {count}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}

                {/* Reviews List */}
                <div className="space-y-6">
                  {(showAllReviews ? reviews : reviews.slice(0, 5)).map((review) => (
                    <div key={review.rating_id} className="border-b border-gray-200 pb-6 last:border-0">
                      <ReviewItem review={formatReviewData(review)} />
                      
                      {/* Show listing context if available */}
                      {review.listing_title && (
                        <div className="mt-4 flex items-center justify-between text-sm">
                          <div className="flex items-center text-gray-600">
                            <Package className="w-4 h-4 mr-2" />
                            <span>For: {review.listing_title}</span>
                            {review.listing_price && (
                              <span className="ml-2 text-[#ff660e] font-medium">
                                {formatPrice(review.listing_price)}
                              </span>
                            )}
                          </div>
                          <button 
                            onClick={() => navigate(`/listings/${review.listing_id}`)}
                            className="text-[#ff660e] hover:text-[#ff5500] font-medium"
                          >
                            View listing
                          </button>
                        </div>
                      )}
                    </div>
                  ))}
                  
                  {/* Load More Button */}
                  {reviews.length > 5 && (
                    <div className="pt-4">
                      <button
                        onClick={() => setShowAllReviews(!showAllReviews)}
                        className="w-full border-2 border-[#ff660e] text-[#ff660e] py-3 rounded-xl font-semibold hover:bg-orange-50 transition-colors flex items-center justify-center gap-2"
                      >
                        {showAllReviews ? 'Show Less' : `Show ${reviews.length - 5} More Reviews`}
                        <ChevronRight className={`w-5 h-5 transition-transform ${showAllReviews ? 'rotate-90' : ''}`} />
                      </button>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        )}

        {/* Delivery Info */}
        {seller.delivery_options && (
          <div className="bg-white rounded-2xl shadow-lg p-6 mt-6">
            <h3 className="text-lg font-semibold text-black mb-4">Delivery Information</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <h4 className="font-medium text-black mb-2">Delivery Options</h4>
                <p className="text-gray-700 capitalize">{seller.delivery_options}</p>
              </div>
              <div>
                <h4 className="font-medium text-black mb-2">Delivery Fee</h4>
                <p className="text-gray-700">
                  {seller.delivery_fee && parseFloat(seller.delivery_fee) > 0 
                    ? `₦${parseFloat(seller.delivery_fee).toLocaleString()}` 
                    : 'Free delivery'}
                </p>
              </div>
              {seller.delivery_coverage_areas && Array.isArray(seller.delivery_coverage_areas) && seller.delivery_coverage_areas.length > 0 && (
              <div className="md:col-span-2">
                <h4 className="font-medium text-black mb-2">Coverage Areas</h4>
                <div className="flex flex-wrap gap-2">
                  {seller.delivery_coverage_areas.map((area, index) => (
                    <span
                      key={index}
                      className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-gray-100 text-gray-800"
                    >
                      {area}
                    </span>
                  ))}
                </div>
              </div>
            )}
            </div>
          </div>
        )}
      </main>
      
      {/* Desktop Footer */}
      <Footer />
    </div>
  );

  return (
    <>
      {/* Render Mobile or Desktop View */}
      <MobileView />
      <DesktopView />
    </>
  );
};

export default SellerDetail;